import { create } from "zustand";

// The action PANELS (Map Soil, Build Rx) deliberately do not follow their buttons into the header:
// they are full inline panels, not dropdowns, and they need the content area's width.

export type FieldAction = "soilmap" | "fert" | "spread" | "section180";

interface FieldToolsState {
  /* * the one search box — matches field names AND action-flag labels (lib/fieldSearch) */
  query: string;
  setQuery: (q: string) => void;

  /* * TanStack row-selection map, keyed by field_id */
  rowSelection: Record<string, boolean>;
  setRowSelection: (
    next:
      | Record<string, boolean>
      | ((cur: Record<string, boolean>) => Record<string, boolean>),
  ) => void;
  /* * select/deselect a set of ids at once (folder-level select) */
  setFieldsSelected: (ids: string[], select: boolean) => void;
  clearSelection: () => void;

  /* * which inline panel is open, if any */
  action: FieldAction | null;
  toggleAction: (a: FieldAction) => void;
  closeAction: () => void;

  confirmDelete: boolean;
  setConfirmDelete: (v: boolean) => void;
  confirmCombine: boolean;
  setConfirmCombine: (v: boolean) => void;
}

export const useFieldToolsStore = create<FieldToolsState>((set) => ({
  query: "",
  setQuery: (query) => set({ query }),

  rowSelection: {},
  setRowSelection: (next) =>
    set((s) => ({
      rowSelection: typeof next === "function" ? next(s.rowSelection) : next,
    })),
  setFieldsSelected: (ids, select) =>
    set((s) => {
      const rowSelection = { ...s.rowSelection };
      for (const id of ids) {
        if (select) rowSelection[id] = true;
        else delete rowSelection[id]; // delete, not false — a false entry is still a key TanStack carries
      }
      return { rowSelection };
    }),
  // Selection is cleared on org switch: the ids belong to the old org and would otherwise linger as
  // orphan.
  clearSelection: () =>
    set({
      rowSelection: {},
      action: null,
      confirmDelete: false,
      confirmCombine: false,
    }),

  action: null,
  toggleAction: (a) => set((s) => ({ action: s.action === a ? null : a })),
  closeAction: () => set({ action: null }),

  confirmDelete: false,
  confirmCombine: false,
  setConfirmCombine: (v) => set({ confirmCombine: v }),
  setConfirmDelete: (confirmDelete) => set({ confirmDelete }),
}));
